import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { GoogleReviews } from '../components/GoogleReviews'
import { LocationMap } from '../components/LocationMap'
import type { LanguageCode } from '../data/languages'
import { seoByLanguage, siteBaseUrl, siteDefaultImage } from '../data/seo'
import { translate } from '../data/translations'
import { useContactSettings } from '../hooks/useContactSettings'
import { useDocumentSeo } from '../hooks/useDocumentSeo'
import { useHomeHeroSettings } from '../hooks/useHomeHeroSettings'
import { useQrGroups } from '../hooks/useQrGroups'

type HomePageProps = {
  language: LanguageCode
}

type LocalizedText = Record<LanguageCode, string>

type HomeFeature = {
  title: LocalizedText
  copy: LocalizedText
  image: string
  href: string
}

const isLegacyBrandImage = (image: string | null | undefined) =>
  !image ||
  /modi-cafe-(logo|favicon)\.png|blackbird|black-roof|a-la-carte|tatli-1024x768|burgerler|sandvic/i.test(image)

function getYouTubeEmbedUrl(url: string) {
  try {
    const parsedUrl = new URL(url)
    const host = parsedUrl.hostname.replace(/^www\./, '')
    const videoId =
      host === 'youtu.be'
        ? parsedUrl.pathname.split('/').filter(Boolean)[0]
        : parsedUrl.searchParams.get('v') ?? parsedUrl.pathname.match(/\/(embed|shorts)\/([^/?]+)/)?.[2]

    if (!videoId || (!host.includes('youtube.com') && host !== 'youtu.be')) return null

    return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&playsinline=1&rel=0&modestbranding=1`
  } catch {
    return null
  }
}


const homeText: Record<LanguageCode, {
  introEyebrow: string
  introTitle: string
  introParagraphs: string[]
  featureEyebrow: string
  featureTitle: string
  reviewsEyebrow: string
  reviewsTitle: string
  mapEyebrow: string
  mapTitle: string
  detail: string
  sliderControls: string
  previousSlide: string
  nextSlide: string
}> = {
  tr: {
    introEyebrow: 'Modi Cafe',
    introTitle: 'Yalova Termal Modi Cafe | Kahvalti & Cafe',
    introParagraphs: [
      'Modi Cafe, Yalova Termal bolgesinde kahvalti, kahve ve tatli molalarini sakin bir butik cafe atmosferiyle bulusturur.',
      'Taze hazirlanan kahvaltilar, sicak icecekler ve gunluk lezzetlerle Termal geziniz icin samimi ve kolay ulasilabilir bir durak sunar.',
      'Menu, slider ve sayfa icerikleri admin panelinden duzenlenebilir; boylece Modi Cafe menusu ve kampanyalari hizlica guncellenebilir.',
    ],
    featureEyebrow: 'Deneyim',
    featureTitle: 'Kahvalti, kahve ve gunluk lezzetler',
    reviewsEyebrow: 'Google Yorumları',
    reviewsTitle: 'Misafir yorumları',
    mapEyebrow: 'Harita',
    mapTitle: 'Modi Cafe konumu',
    detail: 'Detay',
    sliderControls: 'Slider kontrolleri',
    previousSlide: 'Önceki görsel',
    nextSlide: 'Sonraki görsel',
  },
  en: {
    introEyebrow: 'Modi Cafe',
    introTitle: 'Modi Cafe in Yalova Thermal | Breakfast & Cafe',
    introParagraphs: [
      'Modi Cafe brings breakfast, coffee and dessert breaks together with a calm boutique cafe atmosphere in Yalova Thermal.',
      'Fresh breakfast plates, hot drinks and daily flavors make it a friendly stop during a Thermal visit.',
      'Menu, slider and page content remain editable from the admin panel for fast menu and campaign updates.',
    ],
    featureEyebrow: 'Experience',
    featureTitle: 'Breakfast, coffee and daily flavors',
    reviewsEyebrow: 'Google Reviews',
    reviewsTitle: 'Guest reviews',
    mapEyebrow: 'Map',
    mapTitle: 'Modi Cafe location',
    detail: 'Details',
    sliderControls: 'Slider controls',
    previousSlide: 'Previous image',
    nextSlide: 'Next image',
  },
  ru: {
    introEyebrow: 'Modi Cafe',
    introTitle: 'Modi Cafe в Ялова Термал | Кафе и завтраки',
    introParagraphs: [
      'Modi Cafe объединяет завтраки, кофе и десерты в спокойной атмосфере бутикового кафе в Ялова Термал.',
      'Свежие завтраки, горячие напитки и ежедневные вкусы делают его приятной остановкой во время прогулки по Термалу.',
      'Меню, слайдер и контент страниц можно редактировать из панели администратора.',
    ],
    featureEyebrow: 'Опыт',
    featureTitle: 'Завтраки, кофе и ежедневные вкусы',
    reviewsEyebrow: 'Отзывы Google',
    reviewsTitle: 'Отзывы гостей',
    mapEyebrow: 'Карта',
    mapTitle: 'Расположение Modi Cafe',
    detail: 'Подробнее',
    sliderControls: 'Управление слайдером',
    previousSlide: 'Предыдущее изображение',
    nextSlide: 'Следующее изображение',
  },
}

const homeFeatures: HomeFeature[] = [
  {
    title: { tr: 'Kahvalti', en: 'Breakfast', ru: 'Завтраки' },
    copy: {
      tr: 'Serpme kahvalti ve taze tabaklarla Yalova Termal gezinizde gune keyifli bir baslangic yapin.',
      en: 'Enjoy a fresh breakfast stop in Yalova Thermal with generous plates and warm service.',
      ru: 'Начните день в Ялова Термал со свежего завтрака и теплого сервиса.',
    },
    image: '/optimized/legacy/wp-content/uploads/2025/02/kahvaltilar-1024x768.webp',
    href: '/tr/kahvaltilar/',
  },
  {
    title: { tr: 'Sicak Icecekler', en: 'Hot Drinks', ru: 'Горячие напитки' },
    copy: {
      tr: 'Kahve, cay ve mevsimlik sicak iceceklerle sakin bir cafe molasi.',
      en: 'Coffee, tea and seasonal hot drinks for a calm cafe pause.',
      ru: 'Кофе, чай и сезонные горячие напитки для спокойной паузы.',
    },
    image: '/optimized/legacy/wp-content/uploads/2025/02/Sicak-icecekler-1024x768.webp',
    href: '/tr/sicak-icecekler/',
  },
  {
    title: { tr: 'Soguk Icecekler', en: 'Cold Drinks', ru: 'Холодные напитки' },
    copy: {
      tr: 'Limonata, soguk kahve ve mevsimlik ferah secimlerle kisa bir ara verin.',
      en: 'Pause with lemonade, iced coffee and refreshing seasonal choices.',
      ru: 'Сделайте паузу с лимонадом, айс-кофе и сезонными напитками.',
    },
    image: '/optimized/legacy/wp-content/uploads/2025/02/soguk-icecekler-1024x768.webp',
    href: '/tr/soguk-icecekler/',
  },
  {
    title: { tr: 'Butik Mola', en: 'Boutique Pause', ru: 'Бутиковая пауза' },
    copy: {
      tr: 'Yalova Termal icinde sade, temiz ve samimi bir cafe deneyimi.',
      en: 'A simple, clean and friendly cafe experience in Yalova Thermal.',
      ru: 'Простая, чистая и уютная атмосфера кафе в Ялова Термал.',
    },
    image: '/optimized/legacy/wp-content/uploads/2025/02/kahvaltilar-1024x768.webp',
    href: '/tr/hakkimizda/',
  },
  {
    title: { tr: 'Tatli & Pastane', en: 'Desserts', ru: 'Десерты' },
    copy: {
      tr: 'Gunluk tatlilar ve kahve eslesmeleriyle kisa molalari keyifli hale getirin.',
      en: 'Daily desserts and coffee pairings turn short breaks into something sweeter.',
      ru: 'Десерты дня и кофе делают короткие паузы приятнее.',
    },
    image: '/optimized/legacy/wp-content/uploads/2025/02/dondurma-1024x768.webp',
    href: '/tr/tatlilar/',
  },
  {
    title: { tr: 'Cafe Menusu', en: 'Cafe Menu', ru: 'Меню кафе' },
    copy: {
      tr: 'Kahvalti, icecek ve tatli kategorilerini hizlica inceleyin.',
      en: 'Browse breakfast, drinks and dessert categories quickly.',
      ru: 'Быстро просмотрите категории завтраков, напитков и десертов.',
    },
    image: '/optimized/legacy/wp-content/uploads/2025/02/Sicak-icecekler-1024x768.webp',
    href: '/tr/qr/',
  },
]

const fixedHomeFeatureImages: Record<string, string> = {
  '/tr/hakkimizda/': '/optimized/legacy/wp-content/uploads/2025/02/kahvaltilar-1024x768.webp',
  '/tr/kahvaltilar/': '/optimized/legacy/wp-content/uploads/2025/02/kahvaltilar-1024x768.webp',
  '/tr/qr/': '/optimized/legacy/wp-content/uploads/2025/02/Sicak-icecekler-1024x768.webp',
}

export function HomePage({ language }: HomePageProps) {
  const { settings: siteContact } = useContactSettings()
  const { groups } = useQrGroups()
  const qrCoverByHref = new Map(
    groups.flatMap((group) => group.links.map((link) => [link.href.tr, group.coverImage] as const)),
  )
  const displayedFeatures = homeFeatures.map((feature) => ({
    ...feature,
    image:
      fixedHomeFeatureImages[feature.href] ??
      (isLegacyBrandImage(qrCoverByHref.get(feature.href)) ? feature.image : qrCoverByHref.get(feature.href)!),
  }))

  useDocumentSeo(
    {
      title: seoByLanguage[language].title,
      description: seoByLanguage[language].description,
      canonical: `${siteBaseUrl}/${language}/`,
      image: siteDefaultImage,
      rawTitle: true,
    },
    language,
  )

  const text = homeText[language]
  const { settings, activeSlides } = useHomeHeroSettings()
  const [activeSlide, setActiveSlide] = useState(0)
  const heroVideoRef = useRef<HTMLVideoElement | null>(null)
  const displayedSlide = activeSlides.length > 0 ? activeSlide % activeSlides.length : 0
  const currentSlide = activeSlides[displayedSlide]
  const youtubeEmbedUrl = currentSlide?.video ? getYouTubeEmbedUrl(currentSlide.video) : null
  const slideDuration =
    currentSlide?.durationMs && currentSlide.durationMs >= 1000
      ? currentSlide.durationMs
      : settings.intervalMs
  const goToSlide = (direction: -1 | 1) => {
    setActiveSlide((current) => (current + direction + activeSlides.length) % activeSlides.length)
  }

  useEffect(() => {
    if (activeSlides.length < 2) {
      return undefined
    }

    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % activeSlides.length)
    }, slideDuration)

    return () => window.clearInterval(timer)
  }, [activeSlides.length, slideDuration])

  useEffect(() => {
    if (!currentSlide?.video || youtubeEmbedUrl) return

    const video = heroVideoRef.current
    if (!video) return

    video.muted = true
    video.currentTime = 0
    video.load()

    const frame = window.requestAnimationFrame(() => {
      void video.play().catch(() => {
        // Autoplay can still be blocked by browser policy; controls remain hidden by design.
      })
    })

    return () => window.cancelAnimationFrame(frame)
  }, [currentSlide?.video, displayedSlide, youtubeEmbedUrl])

  if (!currentSlide) {
    return null
  }

  const slideEyebrow = currentSlide.eyebrow?.[language] || translate('hero.eyebrow', language)
  const slideTitle =
    currentSlide.title?.[language] || currentSlide.alt?.[language] || translate('hero.title', language)
  const slideCopy = currentSlide.copy?.[language] || translate('hero.copy', language)

  return (
    <main>
      <section className="home-slider" aria-label="Modi Cafe">
        <div className="home-slider-track">
          {youtubeEmbedUrl ? (
            <div
              className="home-slide is-active"
              key={`youtube-${displayedSlide}-${youtubeEmbedUrl}`}
              style={currentSlide.image ? { backgroundImage: `url(${currentSlide.image})` } : undefined}
            >
              <iframe
                allow="autoplay; encrypted-media; picture-in-picture"
                aria-label={currentSlide.alt[language] || 'Modi Cafe video'}
                loading="eager"
                referrerPolicy="strict-origin-when-cross-origin"
                src={youtubeEmbedUrl}
                title={currentSlide.alt[language] || 'Modi Cafe video'}
              />
            </div>
          ) : currentSlide.video ? (
            <div className="home-slide is-active" key={`video-${displayedSlide}-${currentSlide.video}`}>
              <div className="home-video-frame">
                <video ref={heroVideoRef} autoPlay loop muted playsInline preload="auto" poster={currentSlide.image}>
                  <source src={currentSlide.video} type="video/mp4" />
                </video>
              </div>
            </div>
          ) : (
            <picture className="home-slide is-active" key={currentSlide.image}>
              <img
                alt={currentSlide.alt[language]}
                decoding="sync"
                fetchPriority="high"
                height={currentSlide.height}
                loading="eager"
                sizes="(min-width: 1360px) 1335px, 100vw"
                src={currentSlide.image}
                srcSet={currentSlide.srcSet || undefined}
                width={currentSlide.width}
              />
            </picture>
          )}
        </div>
        <div className="home-slider-content">
          <span className="eyebrow">{slideEyebrow}</span>
          <h1>{slideTitle}</h1>
          <p>{slideCopy}</p>
          <div className="hero-actions">
            <a className="primary-action" href={localizeHref('/tr/qr/', language)}>
              {translate('hero.reserve', language)}
              <ArrowRight size={18} />
            </a>
            <a className="secondary-action" href={siteContact.phoneHref}>
              {translate('hero.call', language)}
            </a>
          </div>
        </div>
        <div className="home-slider-controls" aria-label={text.sliderControls}>
          <button aria-label={text.previousSlide} onClick={() => goToSlide(-1)} type="button">
            <ChevronLeft size={24} />
          </button>
          <span>
            {String(displayedSlide + 1).padStart(2, '0')} / {String(activeSlides.length).padStart(2, '0')}
          </span>
          <button aria-label={text.nextSlide} onClick={() => goToSlide(1)} type="button">
            <ChevronRight size={24} />
          </button>
        </div>
      </section>

      <section className="cafe-action-band" id="reservation">
        <div>
          <span className="eyebrow">Modi Cafe</span>
          <h2>{translate('sections.qrTitle', language)}</h2>
          <p>{slideCopy}</p>
        </div>
        <div className="hero-actions">
          <a className="primary-action" href={localizeHref('/tr/qr/', language)}>
            {translate('hero.reserve', language)}
            <ArrowRight size={18} />
          </a>
          <a className="secondary-action" href={siteContact.whatsappHref} rel="noreferrer" target="_blank">
            WhatsApp
          </a>
        </div>
      </section>

      <section className="home-intro-section">
        <div className="section-heading">
          <span className="eyebrow">{text.introEyebrow}</span>
          <h2>{text.introTitle}</h2>
        </div>
        <div className="home-intro-copy">
          {text.introParagraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </section>

      <section className="home-feature-section">
        <div className="section-heading">
          <span className="eyebrow">{text.featureEyebrow}</span>
          <h2>{text.featureTitle}</h2>
        </div>
        <div className="home-feature-grid">
          {displayedFeatures.map((item) => (
            <a className="home-feature-card" href={localizeHref(item.href, language)} key={item.href}>
              <img alt={item.title[language]} height="300" loading="lazy" src={item.image} width="440" />
              <span>{item.title[language]}</span>
              <p>{item.copy[language]}</p>
              <strong>{text.detail}</strong>
            </a>
          ))}
        </div>
      </section>

      <section className="reviews-section">
        <div className="section-heading">
          <span className="eyebrow">{text.reviewsEyebrow}</span>
          <h2>{text.reviewsTitle}</h2>
        </div>
        <GoogleReviews />
      </section>

      <section className="location-section">
        <div className="section-heading">
          <span className="eyebrow">{text.mapEyebrow}</span>
          <h2>{text.mapTitle}</h2>
        </div>
        <LocationMap />
      </section>
    </main>
  )
}

function localizeHref(href: string, language: LanguageCode) {
  return href.replace(/^\/tr\//, `/${language}/`)
}
